import { QueryClient } from '@tanstack/react-query';
import { MediaApi } from 'src/api';
import { ImmerseFilterStatus } from 'src/api/media/schema';
import { ContentMedia } from 'src/api/media/types';
import { ROUTES } from 'src/constants/routes.const';
import { stringifyUrl } from 'src/utils/stringifyUrl';

export type PathImmersionSessionUrlOptions = {
  pathwayNodeId?: number;
  /** When true, omit pathway_node_id so completing the session does not advance the path. */
  forReview?: boolean;
  /**
   * When provided, skip picking and use this media.
   * `null` means no video is available (caller should fall back to the immerse list).
   */
  media?: ContentMedia | null;
};

const MEDIA_VIEWS: ImmerseFilterStatus[] = ['ready_to_watch', 'unwatched', 'watched'];

/** React Query key so preview and launch share the same picked video for a path node. */
export const pathImmersionMediaQueryKey = (languagePairId: number, sessionId: string) =>
  ['pathImmersionMedia', languagePairId, sessionId] as const;

/** Resolve (or reuse) the immersion video cached for this path session node. */
export const ensurePathImmersionMedia = (
  queryClient: QueryClient,
  languagePairId: number,
  sessionId: string,
): Promise<ContentMedia | null> =>
  queryClient.ensureQueryData({
    queryKey: pathImmersionMediaQueryKey(languagePairId, sessionId),
    queryFn: () => getPathImmersionMedia(languagePairId),
  });

/**
 * Picks a video for a path Immersion session.
 * Prefers ready-to-watch, then unwatched, then watched as a last resort.
 */
export const getPathImmersionMedia = async (
  languagePairId: number,
): Promise<ContentMedia | null> => {
  for (const view of MEDIA_VIEWS) {
    const response = await MediaApi.getLanguagePairMedia(languagePairId, {
      limit: 1,
      offset: 0,
      view,
      media_types: ['immerse', 'youtube'],
      ...(view === 'unwatched' ? { random_seed: Math.floor(Math.random() * 1_000_000_000) } : {}),
    });

    const media = response.userContentMedia[0];
    if (media) return media;
  }

  return null;
};

export const buildPathImmersionMediaUrl = (
  languagePairId: number,
  media: ContentMedia,
  options: PathImmersionSessionUrlOptions = {},
): string => {
  const pathwayNodeId = options.forReview ? undefined : options.pathwayNodeId;

  return stringifyUrl(ROUTES.video.media, {
    contentMediaId: media.contentMediaId,
    languagePairId,
    source: 'path',
    back: ROUTES.path,
    ...(pathwayNodeId !== undefined ? { pathway_node_id: pathwayNodeId } : {}),
  });
};

/**
 * Picks a video for a path Immersion session and returns a direct /media URL.
 * Pass `media` to reuse a previously picked video (e.g. from the dashboard preview cache).
 */
export const getPathImmersionSessionUrl = async (
  languagePairId: number,
  options: PathImmersionSessionUrlOptions = {},
): Promise<string | null> => {
  const media =
    options.media !== undefined ? options.media : await getPathImmersionMedia(languagePairId);

  if (!media) return null;

  return buildPathImmersionMediaUrl(languagePairId, media, options);
};
